import React, { forwardRef, useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PublicIcon from "@mui/icons-material/Public";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Desktop: nouvel onglet | Mobile: téléchargement
const openPdf = (base64Data, filename = "document.pdf") => {
  let clean = base64Data.includes(",") ? base64Data.split(",")[1] : base64Data;
  clean = clean.replace(/\s/g, "").replace(/-/g, "+").replace(/_/g, "/");

  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isMobile = isIOS || isAndroid;

  let bytes;
  try {
    const byteChars = atob(clean);
    bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
  } catch (e) {
    console.warn("PDF base64 invalide :", e);
    return;
  }
  const blob = new Blob([bytes], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(blob);

  if (isMobile) {
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.target = "_self";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    return;
  }

  const win = window.open("", "_blank");
  if (win) {
    win.opener = null;
    win.location.href = blobUrl;
  } else {
    window.location.href = blobUrl;
  }
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
};

// Carte individuelle
const AssuranceCard = ({
  title,
  description,
  price,
  engagement,
  limit,
  priseEnCharge,
  features,
  pdfNotice,
  pdfIpid,
  onClick,
}) => {
  const { t } = useTranslation("garanties/AssuranceSelectionCarousel");
  return (
    <Box
      sx={{
        backgroundColor: "#449F2D",
        borderRadius: "20px",
        px: 3,
        py: 4,
        textAlign: "center",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        width: 320,
        mx: "auto",
        boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.25)",
      }}
    >
      <Typography sx={{ fontSize: "1.8rem", fontWeight: 800, mb: 1, color: "#204B15" }}>
        {title}
      </Typography>
      <Typography sx={{ fontWeight: 400, mb: 3, fontSize: "0.95rem" }}>{description}</Typography>

      <Box sx={{ mb: 2, minHeight: "180px" }}>
        {features.map((label, i) => (
          <Box
            key={i}
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, fontSize: "0.95rem" }}
          >
            <Typography>{label}</Typography>
            <CheckCircleIcon sx={{ color: "#FFC12F", fontSize: "1.1rem" }} />
          </Box>
        ))}
      </Box>

      {priseEnCharge && (
        <Typography sx={{ fontSize: "0.85rem", color: "#fff", mb: 1 }}>
          {t("card.coverageUpTo")}{" "}
          <Box component="span" fontWeight="bold">{priseEnCharge}zł</Box>
        </Typography>
      )}

      <Typography sx={{ fontSize: "2rem", fontWeight: 800, mb: 0.5, color: "#FFC12F" }}>
        {price}
        <Box component="span" sx={{ fontSize: "1rem" }}>
          {t("card.perMonthSuffix")}
        </Box>
      </Typography>

      <Typography sx={{ fontSize: "0.75rem", color: "#f0f0f0", mb: 2 }}>
        {t("card.engagement")} : {engagement} {t("card.months")}
      </Typography>

      {(pdfNotice || pdfIpid) && (
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, px: 0, mb: 2 }}>
          {pdfNotice && (
            <Button
              variant="outlined"
              sx={{
                flex: 1,
                borderRadius: "999px",
                color: "#fff",
                borderColor: "#fff",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              }}
              onClick={() => openPdf(pdfNotice, "Notice.pdf")}
            >
              {t("card.notice")}
            </Button>
          )}
          {pdfIpid && (
            <Button
              variant="outlined"
              sx={{
                flex: 1,
                borderRadius: "999px",
                color: "#fff",
                borderColor: "#fff",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              }}
              onClick={() => openPdf(pdfIpid, "IPID.pdf")}
            >
              {t("card.ipid")}
            </Button>
          )}
        </Box>
      )}

      <Button
        variant="contained"
        sx={{
          borderRadius: "999px",
          textTransform: "none",
          fontWeight: 600,
          backgroundColor: "#fff",
          color: "#449F2D",
          width: "80%",
          mx: "auto",
          "&:hover": { backgroundColor: "#e0e0e0" },
        }}
        onClick={onClick}
      >
        {t("card.subscribe")}
      </Button>
    </Box>
  );
};

// Props carte depuis API
const mapCardProps = (a, handleSouscrire) => ({
  title: a.name,
  description: a.description || "Assurance vélo complète",
  price: `${a.price}zł`,
  engagement: a.engagement,
  limit: `${a.price_bike_minimum || 0}zł`,
  priseEnCharge: a.prise_en_charge || null,
  features: Object.entries({
    Casse: a.casse,
    Vol: a.vol,
    Panne: a.panne,
    "Tentative de vol": a.tentative_vol,
    Oxydation: a.oxydation,
  })
    .filter(([_, val]) => val)
    .map(([label]) => label),
  pdfNotice: a.pdf_notice,
  pdfIpid: a.pdf_ipid,
  onClick: () => handleSouscrire(a),
});

const AssuranceSelectionCarousel = (props, ref) => {
  const { t } = useTranslation("garanties/AssuranceSelectionCarousel");
  const [assurances, setAssurances] = useState([]);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAssurances = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_VENDOR_SYB_API}/insurances`);
        const data = await response.json();
        setAssurances(data.insurances || []);
      } catch (e) {
        console.error("Erreur récupération assurances:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchAssurances();
  }, []);

  const handleSouscrire = () => {
    navigate("/parcours-client");
  };

  const settings = {
    dots: false,
    infinite: assurances.length > 3,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false,
    responsive: [
      { breakpoint: 960, settings: { slidesToShow: 1 } },
      { breakpoint: 1280, settings: { slidesToShow: 2 } },
    ],
  };

  return (
    <Box ref={ref} sx={{ maxWidth: "1200px", mx: "auto", mt: 10, px: 2 }}>
      <Typography sx={{ fontWeight: 900, fontSize: { xs: "2.2rem", md: "3.5rem" }, textAlign: "center", color: "#000", mb: 6 }}>
        {t("title.part1")}{" "}
        <Box component="span" sx={{ color: "#FFC22F" }}>
          {t("title.highlight")}
        </Box>
      </Typography>

      <Typography sx={{ textAlign: "center", fontSize: "1.1rem", maxWidth: "700px", mx: "auto", color: "#444", mb: 4 }}>
        {t("lede.p1a")} <Box component="span" fontWeight="bold">{t("lede.p1b")}</Box>
        {t("lede.p1c")} <Box component="span" fontWeight="bold">{t("lede.p1d")}</Box> {t("lede.p1e")}{" "}
        <Box component="span" fontWeight="bold">{t("lede.p1f")}</Box>.{" "}
        {t("lede.p2a")} <Box component="span" fontWeight="bold">{t("lede.p2b")}</Box>,{" "}
        <Box component="span" fontWeight="bold">{t("lede.p2d")}</Box> {t("lede.p2e")}{" "}
        <Box component="span" fontWeight="bold">{t("lede.p2f")}</Box>.{" "}
        {t("lede.p3a")} <Box component="span" fontWeight="bold">{t("lede.p3b")}</Box>, {t("lede.p3c")}{" "}
        <Box component="span" fontWeight="bold">{t("lede.p3d")}</Box> {t("lede.p3e")}{" "}
        <Box component="span" fontWeight="bold">{t("lede.p3f")}</Box>, {t("lede.p3g")}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap", mb: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <VerifiedUserIcon sx={{ color: "#000" }} />
          <Typography sx={{ fontWeight: 500, fontSize: "1rem", color: "#000" }}>
            {t("badges.freedom")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PublicIcon sx={{ color: "#000" }} />
          <Typography sx={{ fontWeight: 500, fontSize: "1rem", color: "#000" }}>
            {t("badges.worldwide")}
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box textAlign="center">
          <CircularProgress />
        </Box>
      ) : assurances.length === 0 ? (
        <Typography textAlign="center">{t("noData")}</Typography>
      ) : assurances.length >= 4 ? (
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={() => sliderRef.current?.slickPrev()}
            sx={{
              position: "absolute",
              top: "50%",
              left: -20,
              transform: "translateY(-50%)",
              zIndex: 2,
              backgroundColor: "#fff",
              boxShadow: 2,
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>

          <Slider ref={sliderRef} {...settings}>
            {assurances.map((a) => (
              <Box key={a.id} px={1}>
                <AssuranceCard {...mapCardProps(a, handleSouscrire)} />
              </Box>
            ))}
          </Slider>

          <IconButton
            onClick={() => sliderRef.current?.slickNext()}
            sx={{
              position: "absolute",
              top: "50%",
              right: -20,
              transform: "translateY(-50%)",
              zIndex: 2,
              backgroundColor: "#fff",
              boxShadow: 2,
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 4 }}>
          {assurances.map((a) => (
            <Box key={a.id} px={1}>
              <AssuranceCard {...mapCardProps(a, handleSouscrire)} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default forwardRef(AssuranceSelectionCarousel);